import { each, extend, find, has, keys, reduce, sortBy } from "lodash";
import storage from './storage';
import store from "../store";

const _ = { each, extend, find, has, keys, reduce, sortBy };

const TYPE_EVENT = 'event';
const TYPE_EXCEPTION = 'exception';

let collectionStack = [];
let gtag = null;

/**
 * Tries to use Google's recommended events
 *
 * - login: A login event
 * - search: Search initiated from the Nav search bar
 * - select_item: Following a search result
 * - select_content: Viewing some content, e.g. content_type: report
 *
 * These are the custom events
 * - user_engagement: Generic event to capture user interaction with the site
 */

/**
 * Should be called once when the gtag object is bootstrapped and ready.
 *
 * @param {*} gtagObject   The actual object so we can keep a reference to it.
 */
function setAnalyticsReady(gtagObject) {
  gtag = gtagObject;

  // go through the collection stack to call whatever was added while bootstrapping
  _.each(collectionStack, (analyticEvent) => {
    if (analyticEvent.type === TYPE_EVENT) {
      event(analyticEvent.action, analyticEvent.options, analyticEvent.filters);
    }
    else if (analyticEvent.type === TYPE_EXCEPTION) {
      exception(analyticEvent.description, analyticEvent.options);
    }
  });
  collectionStack = [];
}

/**
 * Calls the gtag event method.  If the analytics is not ready, it'll be pushed to the collection stack which will
 * be run once ready.
 *
 * @param {*} action    The particular action for the event to be recorded
 * @param {*} options   Any extra options
 * @param {*} filters   Optional key/value pair for report filters.  These will get assigned into the options being sent
 */
function event(action, options, filters) {
  // don't log an event for staff unless we're local
  const isDebugMode = process.env.VUE_APP_DEBUG_ANALYTICS === 'true' || false;
  if (!isDebugMode && storage.getBoolean(storage.IS_IRAPO)) {
    return;
  }

  const timeTerms = ['SEM_YR_IRO', 'FISCAL_YEAR', 'ACADEMIC_YEAR', 'CURRENT_ACAD_YR', 'SEMESTER', 'sem'];
  const locationTerms = ['IRO_INSTITUTION', 'IRO_INST_AND_UHCC'];

  const qs = _.reduce(_.sortBy(_.keys(filters), key => key), (accum, curr) => `${accum}&${curr}=${filters[curr]}`, '');
  if (qs.length > 0) {
    options.qs = qs.substring(1);
  }
  options.debug_mode = isDebugMode;

  // add on our custom dimensions
  const timeTerm = _.find(timeTerms, termKey => _.has(filters, termKey));
  const locationTerm = _.find(locationTerms, locationKey => _.has(filters, locationKey));
  if (timeTerm) {
    options.term = filters[timeTerm];
  }
  if (locationTerm) {
    options.iro_institution = filters[locationTerm];
  }

  // for reports, make sure ids are uppercase
  if (options.content_type === 'report' && options.item_id) {
    options.item_id = options.item_id.toUpperCase();
  }

  const userData = store.getters['session/sessionAnalyticData'];
  options.campus_aff = userData.campus;
  options.user_type = userData.userType;

  if (gtag) {
    gtag.event(action, options);
  }
  else {
    // add it to the stack to be processed
    collectionStack.push({
      type: TYPE_EVENT,
      action,
      options,
      filters
    });
  }
}

/**
 * Calls the gtag event method.  If the analytics is not ready, it'll be pushed to the collection stack which will
 * be run once ready.
 *
 * @param {*} action    The particular action for the event to be recorded
 * @param {*} options   Any extra options, e.g. fatal boolean property
 */
function exception(description, options) {
  if (gtag) {
    console.error(description);
    gtag.exception({
      description,
      fatal: options ? Boolean(options.fatal) : false
    });
  }
  else {
    // add it to the stack to be processed
    collectionStack.push({
      type: TYPE_EXCEPTION,
      description,
      options
    });
  }
}

/**
 * Common utility functions that can be used throughout the app
 */
export default {
  event,
  exception,
  setAnalyticsReady,
  events: {
    LOGIN: 'login',
    SEARCH: 'search',
    SELECT_ITEM: 'select_item',
    USER_ENGAGEMENT: 'user_engagement',
    VIEW_CONTENT: 'select_content',
    EXTERNAL: 'external_link'
  },
  viewTypes: {
    DEFAULT: 'default',
    PARAMS: 'params'
  }
};